import { useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { useIsomorphicLayoutEffect } from "../../hooks/isomorphicLayout";

interface TextMarqueeProps {
  children: string;
  duration?: string;
  className?: string;
}
export const TextMarquee = ({
  className,
  children,
  duration = "5s",
}: TextMarqueeProps) => {
  const textRef = useRef<HTMLSpanElement>();
  const marqueeRef = useRef<HTMLDivElement>();
  const [shouldScroll, setShouldScroll] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useIsomorphicLayoutEffect(() => {
    if (marqueeRef.current && textRef.current) {
      setShouldScroll(
        marqueeRef.current.offsetWidth < textRef.current.offsetWidth
      );
      setIsLoaded(true);
    }
  }, []);

  return (
    <MarqueeContainer
      className={className}
      ref={marqueeRef}
      data-should-scroll={shouldScroll}
      data-is-loaded={isLoaded}
    >
      <MarqueeText
        width={`${children?.length}ch`}
        duration={duration}
        ref={textRef}
      >
        {children}
      </MarqueeText>
    </MarqueeContainer>
  );
};

const marquee = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`;

const disableAnimation = css`
  animation: none;
  text-shadow: none;
  [data-is-loaded="true"] & {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const MarqueeContainer = styled.div`
  overflow: hidden;
`;
const MarqueeText = styled.span<{ width: string; duration: string }>`
  display: inline-block;
  white-space: nowrap;
  color: currentColor;
  width: ${(p) => p.width};
  text-shadow: ${(p) => p.width} 0 currentColor,
    calc(${(p) => p.width} * 2) 0 currentColor,
    calc(${(p) => p.width} * 3) 0 currentColor,
    calc(${(p) => p.width} * 4) 0 currentColor;

  animation-name: ${marquee};
  animation-duration: ${(p) => p.duration};
  animation-play-state: running;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-fill-mode: forwards;

  [data-should-scroll="false"] & {
    ${disableAnimation};
  }
  @media (prefers-reduced-motion: reduce) {
    ${disableAnimation};
  }
`;
